import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Send, CheckCircle } from 'lucide-react';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import axios from 'axios';

// Airtable Configuration
const AIRTABLE_API_KEY = 'patSMIu4CYx9BFSua.e2a1ce15ce83ab51d26b6786dfc6953ac2a15d66053f083af30bc74816417d3c';
const AIRTABLE_BASE_ID = 'appDlTNAhbC9CEZSB';
const AIRTABLE_TABLE_NAME = 'tbl2EKcumxl3e0EVr';

const ContactForm = ({ ctaText = "Let's Build Something Great Together", variant = 'default' }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: '',
  });

  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handlePhoneChange = (value, country, e, formattedValue) => {
    setFormData(prev => ({ ...prev, phone: value }));
    if (errors.phone) {
      setErrors(prev => ({ ...prev, phone: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (formData.phone.length < 8) { // Basic length check
      newErrors.phone = 'Please enter a valid phone number';
    }

    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (validateForm()) {
      setIsSubmitting(true);
      try {
        const pageUrl = typeof window !== 'undefined' ? (window.location.href || document.location.href) : '';

        const airtableData = {
          fields: {
            Name: formData.name,
            Email: formData.email,
            Organization: formData.company,
            Phone: `+${formData.phone}`,
            Question: formData.message,
            PageUrl: pageUrl,
          }
        };

        const response = await axios.post(
          `https://api.airtable.com/v0/${AIRTABLE_BASE_ID}/${AIRTABLE_TABLE_NAME}`,
          airtableData,
          {
            headers: {
              'Authorization': `Bearer ${AIRTABLE_API_KEY}`,
              'Content-Type': 'application/json'
            }
          }
        );

        if (response.status === 200) {
          setSubmitted(true);
          // Reset form after 5 seconds
          setTimeout(() => {
            setFormData({
              name: '',
              email: '',
              phone: '',
              company: '',
              message: '',
            });
            setSubmitted(false);
          }, 5000);
        }
      } catch (error) {
        console.error('Error submitting form:', error);
        // Ideally show an error message to the user here
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  if (submitted) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className={`${variant === 'inline'
          ? 'bento-card bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-200'
          : 'bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-12 border-2 border-green-200'
          }`}
      >
        <div className="text-center">
          <div className="w-20 h-20 bg-gradient-to-br from-green-500 to-emerald-500 rounded-full flex items-center justify-center mx-auto mb-6 shadow-xl">
            <CheckCircle className="w-12 h-12 text-white" />
          </div>
          <h3 className="text-3xl font-bold text-slate-900 mb-4">Thank You!</h3>
          <p className="text-lg text-slate-700 mb-2">
            We've received your message and will get back to you within 24 hours.
          </p>
          <p className="text-slate-600">
            We look forward to discussing your project!
          </p>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className={variant === 'inline' ? '' : 'bg-white'}
    >
      {ctaText && (
        <div className="text-center mb-8">
          <h2 className={`${variant === 'inline' ? 'text-2xl md:text-3xl' : 'text-3xl md:text-4xl'} font-bold gradient-text mb-3`}>
            {ctaText}
          </h2>
          <p className="text-slate-600">Fill out the form below and we'll respond within 24 hours</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className={variant === 'inline' ? 'bento-card' : 'max-w-3xl mx-auto'}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Name */}
          <div>
            <label htmlFor="name" className="block text-sm font-semibold text-slate-700 mb-2">
              Your Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="John Doe"
              className={`w-full px-4 py-3 rounded-xl border-2 transition-all focus:outline-none focus:ring-2 focus:ring-[#4C3362] ${errors.name ? 'border-red-300 bg-red-50' : 'border-slate-200 bg-white'
                }`}
            />
            {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
          </div>

          {/* Email */}
          <div>
            <label htmlFor="email" className="block text-sm font-semibold text-slate-700 mb-2">
              Email Address <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="john@company.com"
              className={`w-full px-4 py-3 rounded-xl border-2 transition-all focus:outline-none focus:ring-2 focus:ring-[#4C3362] ${errors.email ? 'border-red-300 bg-red-50' : 'border-slate-200 bg-white'
                }`}
            />
            {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
          </div>

          {/* Phone Number with Country Code */}
          <div>
            <label htmlFor="phone" className="block text-sm font-semibold text-slate-700 mb-2">
              Phone Number <span className="text-red-500">*</span>
            </label>
            <div className={`rounded-xl border-2 transition-all focus-within:ring-2 focus-within:ring-[#4C3362] ${errors.phone ? 'border-red-300 bg-red-50' : 'border-slate-200 bg-white'
              }`}
            >
              {typeof window !== 'undefined' ? (
                <PhoneInput
                  country={'in'}
                  value={formData.phone}
                  onChange={handlePhoneChange}
                  inputProps={{
                    name: 'phone',
                    required: true,
                    autoFocus: false,
                  }}
                  enableSearch={true}
                  disableDropdown={false}
                  countryCodeEditable={false}
                  containerClass="!w-full !border-none"
                  inputClass="!w-full !h-[50px] !text-base !bg-transparent !border-none !pl-[48px] !rounded-xl"
                  buttonClass="!bg-transparent !border-r !border-slate-200 !rounded-l-xl !hover:bg-transparent"
                  dropdownClass="!shadow-xl !rounded-xl !overflow-y-auto"
                />
              ) : (
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                  placeholder="+91 1234567890"
                  className="w-full h-[50px] px-4 text-base bg-transparent border-none rounded-xl"
                />
              )}
            </div>
            {errors.phone && <p className="text-red-500 text-sm mt-1">{errors.phone}</p>}
          </div>

          {/* Company */}
          <div>
            <label htmlFor="company" className="block text-sm font-semibold text-slate-700 mb-2">
              Company
            </label>
            <input
              type="text"
              id="company"
              name="company"
              value={formData.company}
              onChange={handleChange}
              placeholder="Your Company Name"
              className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 bg-white transition-all focus:outline-none focus:ring-2 focus:ring-[#4C3362]"
            />
          </div>
        </div>

        {/* Message */}
        <div className="mt-6">
          <label htmlFor="message" className="block text-sm font-semibold text-slate-700 mb-2">
            Message <span className="text-red-500">*</span>
          </label>
          <textarea
            id="message"
            name="message"
            value={formData.message}
            onChange={handleChange}
            placeholder="Tell us about your project..."
            rows="5"
            className={`w-full px-4 py-3 rounded-xl border-2 transition-all focus:outline-none focus:ring-2 focus:ring-[#4C3362] resize-none ${errors.message ? 'border-red-300 bg-red-50' : 'border-slate-200 bg-white'
              }`}
          />
          {errors.message && <p className="text-red-500 text-sm mt-1">{errors.message}</p>}
        </div>

        {/* Submit Button */}
        <div className="mt-8">
          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full btn-primary text-lg py-4 flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? (
              <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
            ) : (
              <Send className="w-5 h-5" />
            )}
            {isSubmitting ? 'Sending...' : 'Send Message'}
          </button>
        </div>
      </form>
    </motion.div>
  );
};

export default ContactForm;
